import { useMemo } from 'react';

import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  DataTableFiltersV2,
  SearchFilterConfig,
  TableFilterConfig,
  useDebouncedSearchFilter,
  UseTableFiltersReturn,
} from '@/components/data-table/filters-v2';
import { Input } from '@/components/ui/input';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import {
  DateRangePickerV2,
  DateRangePreset,
  DateRangePresetCategory,
  DateRangeV2,
} from '@/modules/common/components/molecules/date/date-range-picker-v2';

export interface GenerateEntityFiltersProps {
  /** Selected date range, or null when nothing is selected */
  dates: DateRangeV2 | null;
  /** Detected preset for the current range, if any */
  selectedPreset: DateRangePreset | null;
  /** Callback when the user picks custom dates */
  onDateChange: (dates: DateRangeV2) => void;
  /** Callback when a preset is selected */
  onPresetSelect: (preset: DateRangePreset) => void;
  /** Optional reset behavior for the date picker */
  onDateReset?: () => void;
  /** Label prefix shown on the date picker trigger */
  dateRangeLabel: string;
  dateRangeInfoTooltip?: React.ReactNode;
  /** V2 filter config (search + dynamic autocomplete) */
  filtersConfig: TableFilterConfig[];
  /** V2 filter state from useTableFilters */
  filterState: UseTableFiltersReturn;
  /** Unique table id, used by v2 for persistence scoping */
  tableId?: string;
  /** Optional right-aligned slot — used for the compact refresh button */
  rightSlot?: React.ReactNode;
  /** Preset categories to hide in the date picker (e.g. Year/Quarter on generate). */
  hiddenPresetCategories?: DateRangePresetCategory[];
}

/**
 * Header filter row for Generate Invoices / Generate Bills.
 *
 * Layout responds to viewport width:
 *   • below xl : Row 1 [DatePicker] [Search]
 *                Row 2 [Filter dropdown + chips] ........ [rightSlot]
 *   • xl+      : single row [DatePicker] [Search] [Filter+chips] [rightSlot]
 *
 * The right slot shares a row with the filter dropdown on narrow screens so neither of them
 * is left alone on a row with dead space beside it.
 *
 * Search is rendered directly here (rather than via `DataTableFiltersV2`'s
 * built-in search input) so we control its placement on small screens.
 */
export const GenerateEntityFilters = ({
  dates,
  selectedPreset,
  onDateChange,
  onPresetSelect,
  onDateReset,
  dateRangeLabel,
  dateRangeInfoTooltip,
  filtersConfig,
  filterState,
  tableId,
  rightSlot,
  hiddenPresetCategories,
}: GenerateEntityFiltersProps) => {
  const { t } = useTranslation('common');

  const searchConfig = useMemo(
    () => filtersConfig.find(c => c.type === 'search') as SearchFilterConfig | undefined,
    [filtersConfig]
  );

  // Strip the search entry so DataTableFiltersV2 doesn't render its own input.
  const dropdownFiltersConfig = useMemo(
    () => filtersConfig.filter(c => c.type !== 'search'),
    [filtersConfig]
  );

  const committedSearchValue = searchConfig
    ? ((filterState.activeFilters[searchConfig.name] as string | undefined) ?? '')
    : '';

  // Debounce keystrokes + enforce minCharacters so typing doesn't fire one
  // backend request per character (the search lives in the query key).
  const { inputValue: searchValue, onInputChange: handleSearchChange } = useDebouncedSearchFilter({
    committedValue: committedSearchValue,
    minCharacters: searchConfig?.minCharacters,
    onCommit: value => searchConfig && filterState.setFilter(searchConfig.name, value),
  });

  return (
    <div className="flex flex-col gap-2 xl:flex-row xl:flex-wrap xl:items-center">
      {/* Row 1: date picker + search + right slot */}
      <div className="flex flex-wrap items-center gap-2 xl:contents">
        {dateRangeInfoTooltip && (
          <SimpleTooltip content={dateRangeInfoTooltip}>
            <button
              type="button"
              className="text-muted-foreground transition-colors hover:text-foreground"
              aria-label={t('moreInfo')}
            >
              <Info className="h-4 w-4" />
            </button>
          </SimpleTooltip>
        )}

        <DateRangePickerV2
          dates={dates}
          onChange={onDateChange}
          selectedPreset={selectedPreset}
          onPresetSelect={onPresetSelect}
          onReset={onDateReset}
          defaultPrefix={dateRangeLabel}
          side="bottom"
          align="start"
          sideOffset={4}
          collisionPadding={16}
          avoidCollisions
          useViewportCollisionBoundary
          hiddenPresetCategories={hiddenPresetCategories}
        />

        {dates && searchConfig && (
          <Input
            placeholder={searchConfig.placeholder ?? t('search')}
            value={searchValue}
            onChange={e => handleSearchChange(e.target.value)}
            className="h-9 w-[220px]"
          />
        )}

        {/* With no date range there is no filter row, so the slot stays on the first row. */}
        {rightSlot && !dates && (
          <div className="ml-auto flex-shrink-0 xl:order-last">{rightSlot}</div>
        )}
      </div>

      {/* Row 2 on small screens (inlined on xl+ via `xl:contents` above) */}
      {dates && (
        <div className="flex flex-wrap items-center gap-2 xl:contents">
          {dropdownFiltersConfig.length > 0 && (
            <DataTableFiltersV2
              filtersConfig={dropdownFiltersConfig}
              filterState={filterState}
              tableId={tableId ?? 'generate-invoices'}
              showSaveButton={false}
              className="xl:flex-1"
            />
          )}
          {rightSlot && <div className="ml-auto flex-shrink-0 xl:order-last">{rightSlot}</div>}
        </div>
      )}
    </div>
  );
};
